/**
 * InfoSponsorSlide
 *
 * Read-only slide showing information/sponsor content.
 * Displays links to the images when available.
 */
import React from "react";
import { Card, CardContent, Stack, Typography, Chip, Box } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import LinkIcon from "@mui/icons-material/Link";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

// Import slide configuration for timing
import { SLIDE_DURATIONS, SLIDE_TYPES } from "../Slides/slideConfig";

/**
 * InfoSponsorSlide Component
 *
 * Displays the fixed Info/Sponsor slide configuration.
 * This slide is non-editable and managed separately.
 *
 * @component
 */
export default function InfoSponsorSlide({ introImages = [] }) {
  const introDuration = SLIDE_DURATIONS[SLIDE_TYPES.INTRO] / 1000; // Convert to seconds

  return (
    <Card
      elevation={8}
      sx={{
        border: "2px solid",
        borderColor: "primary.main",
        borderRadius: 3,
        "&:hover": {
          boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <InfoIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography variant="h6">Slide 1: Information & Sponsor</Typography>
          <Chip label="Fixed" color="info" size="small" />
          <Chip
            icon={<AccessTimeIcon />}
            label={`${introDuration}s`}
            color="primary"
            size="small"
            variant="outlined"
          />
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This slide displays community information and sponsor content. The
          content is managed separately and cannot be edited here.
        </Typography>
        {introImages.length > 0 && (
          <Stack direction="row" spacing={2}>
            {introImages.map((img, idx) => (
              <a
                key={img.url}
                href={img.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "flex",
                  alignItems: "center",
                  textDecoration: "none",
                }}
              >
                <LinkIcon sx={{ mr: 0.5 }} />
                <Typography variant="body2" color="primary">
                  See image {idx + 1}
                </Typography>
              </a>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
